import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface TikTokPixelQueue extends Array<unknown> {
  methods: string[];
  setAndDefer(target: TikTokPixelQueue, method: string): void;
  instance(pixelId: string): TikTokPixelQueue;
  load(pixelId: string, options?: object): void;
  page(): void;
  track(event: string, properties?: object): void;
  _i: Record<string, TikTokPixelQueue & { _u: string }>;
  _t: Record<string, number>;
  _o: Record<string, object>;
}

declare global {
  interface Window {
    TiktokAnalyticsObject: string;
    ttq: TikTokPixelQueue;
  }
}

function initTikTokPixel(pixelId: string): void {
  if (typeof window.ttq?.load === "function") {
    // Already initialized — just load this pixel ID
    window.ttq.load(pixelId);
    return;
  }

  window.TiktokAnalyticsObject = "ttq";
  const ttq: TikTokPixelQueue = (window.ttq =
    window.ttq || ([] as unknown as TikTokPixelQueue));

  ttq.methods = [
    "page",
    "track",
    "identify",
    "instances",
    "debug",
    "on",
    "off",
    "once",
    "ready",
    "alias",
    "group",
    "enableCookie",
    "disableCookie",
    "holdConsent",
    "revokeConsent",
    "grantConsent",
  ];

  ttq.setAndDefer = function (target, method) {
    (target as unknown as Record<string, unknown>)[method] = function (
      ...args: unknown[]
    ) {
      target.push([method, ...args]);
    };
  };

  for (const method of ttq.methods) {
    ttq.setAndDefer(ttq, method);
  }

  ttq.instance = function (id) {
    const inst = (ttq._i?.[id] ?? []) as TikTokPixelQueue;
    for (const method of ttq.methods) {
      ttq.setAndDefer(inst, method);
    }
    return inst;
  };

  ttq.load = function (id, options) {
    const src = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[id] = [] as unknown as TikTokPixelQueue & { _u: string };
    ttq._i[id]._u = src;
    ttq._t = ttq._t || {};
    ttq._t[id] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[id] = options || {};
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = src + "?sdkid=" + id + "&lib=ttq";
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode!.insertBefore(script, firstScript);
  };

  ttq.load(pixelId);
}

interface TikTokPixelProviderProps {
  pixelId: string;
  children: ReactNode;
}

export function TikTokPixelProvider({
  pixelId,
  children,
}: TikTokPixelProviderProps) {
  useEffect(() => {
    initTikTokPixel(pixelId);
  }, [pixelId]);

  return <>{children}</>;
}

export function useTikTokPageView() {
  const location = useLocation();

  useEffect(() => {
    window.ttq?.page?.();
  }, [location.pathname]);
}
